// ChatWithSessions.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { motion } from 'framer-motion';
import { 
  PaperAirplaneIcon,
  SparklesIcon,
  FaceSmileIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import SessionManager from './SessionManager';
import apiClient, { baseURL } from '../../utils/api';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

// 타이핑 인디케이터 스타일
const typingIndicatorStyles = `
  .typing-indicator {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 0;
  }

  .typing-indicator span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #94a3b8;
    animation: typing-dot 1.4s infinite ease-in-out;
  }

  .typing-indicator span:nth-child(1) {
    animation-delay: 0s;
  }

  .typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typing-dot {
    0%, 60%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    30% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

// Role을 gradeLevel로 매핑하는 함수
const mapRoleToGradeLevel = (role) => {
  switch (role) {
    case 'STUDENT':
      return 'elementary_low';
    case 'TEACHER':
      return 'teacher';
    default:
      return 'elementary_low';
  }
};

const MessageBubble = ({ message, gradeLevel }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  
  // 시스템 메시지 (필터링 경고 등)
  if (isSystem) {
    return (
      <div
        key={message.id}
        style={{
          display: 'flex',
          marginBottom: '16px',
          justifyContent: 'center'
        }}
      >
        <div style={{
          background: message.filterType === 'warning' ? '#fff3cd' : '#d1ecf1',
          border: message.filterType === 'warning' ? '1px solid #ffe082' : '1px solid #bee5eb',
          borderRadius: '12px',
          padding: '12px 20px',
          maxWidth: '80%',
          textAlign: 'center'
        }}>
          <p style={{
            color: message.filterType === 'warning' ? '#856404' : '#0c5460',
            margin: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            {message.content}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div
      key={message.id}
      style={{
        display: 'flex',
        marginBottom: '16px',
        justifyContent: isUser ? 'flex-end' : 'flex-start'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        maxWidth: '70%'
      }}>
        {!isUser && (
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '8px',
            fontSize: '16px'
          }}>
            🤖
          </div>
        )}
        <div>
          <div style={{
            background: isUser 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : message.isFiltered ? '#fff3cd' : '#f8f9fa',
            color: isUser ? 'white' : message.isFiltered ? '#856404' : '#333',
            padding: '12px 16px',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: isUser ? 'none' : message.isFiltered ? '1px solid #ffe082' : '1px solid #e9ecef',
            wordBreak: 'break-word'
          }}>
            {isUser ? (
              <span style={{ fontSize: '14px', lineHeight: '1.4' }}>
                {message.content}
              </span>
            ) : (
              <div style={{ fontSize: '14px', lineHeight: '1.6' }} className="markdown-content">
                {message.isStreaming && !message.content && (
                  <span className="typing-indicator">
                    <span></span><span></span><span></span>
                  </span>
                )}
                <ReactMarkdown
                  components={{
                    // 코드 블록 스타일링
                    code({ node, inline, className, children, ...props }) {
                      return inline ? (
                        <code
                          style={{
                            backgroundColor: '#f3f4f6',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            fontSize: '0.875em',
                            fontFamily: 'monospace'
                          }}
                          {...props}
                        >
                          {children}
                        </code>
                      ) : (
                        <pre
                          style={{
                            backgroundColor: '#1f2937',
                            color: '#e5e7eb',
                            padding: '12px',
                            borderRadius: '8px',
                            overflowX: 'auto',
                            marginTop: '8px',
                            marginBottom: '8px'
                          }}
                        >
                          <code
                            style={{
                              fontSize: '0.875em',
                              fontFamily: 'monospace'
                            }}
                            {...props}
                          >
                            {children}
                          </code>
                        </pre>
                      );
                    },
                    // 리스트 스타일링
                    ul({ children }) {
                      return (
                        <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                          {children}
                        </ul>
                      );
                    },
                    ol({ children }) {
                      return (
                        <ol style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                          {children}
                        </ol>
                      );
                    },
                    // 링크 스타일링
                    a({ href, children }) {
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#3b82f6',
                            textDecoration: 'underline'
                          }}
                        >
                          {children}
                        </a>
                      );
                    },
                    // 굵은 텍스트
                    strong({ children }) {
                      return <strong style={{ fontWeight: '600' }}>{children}</strong>;
                    },
                    // 단락 스타일링
                    p({ children }) {
                      return <p style={{ marginTop: '8px', marginBottom: '8px' }}>{children}</p>;
                    },
                    // 제목 스타일링
                    h1({ children }) {
                      return <h1 style={{ fontSize: '1.5em', fontWeight: 'bold', marginTop: '12px', marginBottom: '8px' }}>{children}</h1>;
                    },
                    h2({ children }) {
                      return <h2 style={{ fontSize: '1.25em', fontWeight: 'bold', marginTop: '10px', marginBottom: '6px' }}>{children}</h2>;
                    },
                    h3({ children }) {
                      return <h3 style={{ fontSize: '1.1em', fontWeight: 'bold', marginTop: '8px', marginBottom: '4px' }}>{children}</h3>;
                    }
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            )}
          </div>
          
          <div style={{
            fontSize: '11px',
            color: '#999',
            marginTop: '4px',
            textAlign: isUser ? 'right' : 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: isUser ? 'flex-end' : 'flex-start'
          }}>
            {(message.createdAt || message.timestamp) ? 
              new Date(message.createdAt || message.timestamp).toLocaleTimeString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit'
              }) : '방금 전'
            }
            {message.isFiltered && (
              <span style={{
                color: '#dc3545',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                🛡️ 안전 필터 적용됨
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 학년별 환영 메시지 및 추천 질문 함수
const getWelcomeMessage = (gradeLevel, username) => {
  const gradeMessages = {
    elementary_low: `안녕하세요, ${username}님! 🌟\n저는 여러분의 공부를 도와주는 AI 친구예요. 궁금한 것이 있으면 언제든지 물어보세요!`,
    elementary_high: `안녕하세요, ${username}님! 📚\n저는 여러분의 학습을 도와주는 AI 선생님이에요. 어떤 것이 궁금하신가요?`,
    teacher: `안녕하세요, ${username} 선생님! 👨‍🏫\n교육 전문 AI 어시스턴트입니다. 수업 계획, 학급 관리, 교육 자료 개발 등 무엇이든 도와드리겠습니다!`,
    middle: `안녕하세요, ${username}님! 🎓\n저는 여러분의 학습 파트너 AI입니다. 공부하면서 궁금한 점이 있으면 편하게 질문해주세요!`,
    high: `안녕하세요, ${username}님! 💡\n저는 여러분의 학습을 지원하는 AI 어시스턴트입니다. 학습 관련 질문이나 진로 상담 등 무엇이든 도와드릴게요!`
  };
  
  return gradeMessages[gradeLevel] || gradeMessages.elementary_low;
};

const getSuggestions = (gradeLevel) => {
  const suggestions = {
    elementary_low: [
      "동물들은 어떻게 겨울을 날까요?",
      "왜 하늘은 파란색일까요?",
      "식물은 어떻게 자랄까요?",
      "숫자 놀이를 해볼까요?"
    ],
    elementary_high: [
      "태양계에는 어떤 행성들이 있나요?",
      "곱셈구구를 쉽게 외우는 방법이 있나요?",
      "우리나라의 역사에 대해 알려주세요",
      "과학 실험을 해보고 싶어요"
    ],
    teacher: [
      "📚 창의적인 수업 아이디어 추천해줘",
      "🎯 학습 목표 설정 방법 알려줘",
      "👥 학급 관리 팁 제안해줘",
      "📊 학습 평가 방법 추천해줘"
    ],
    middle: [
      "수학 문제 풀이를 도와주세요",
      "영어 문법을 쉽게 설명해주세요",
      "과학 개념을 이해하기 쉽게 설명해주세요",
      "역사 사건들의 연관성을 알려주세요"
    ],
    high: [
      "대학 진학 준비는 어떻게 해야 하나요?",
      "진로 선택에 대한 조언을 해주세요",
      "복잡한 수학 개념을 설명해주세요",
      "논술 작성법을 알려주세요"
    ]
  };
  
  return suggestions[gradeLevel] || suggestions.elementary_low;
};

const getPlaceholder = (gradeLevel) => {
  const placeholders = {
    elementary_low: "궁금한 것을 물어보세요! 😊",
    elementary_high: "어떤 것이 궁금한가요? 📝",
    teacher: "교육 관련 질문이나 수업 아이디어를 물어보세요! 👨‍🏫",
    middle: "학습 관련 질문을 해보세요! 🤔",
    high: "무엇을 도와드릴까요? 💭"
  };
  
  return placeholders[gradeLevel] || placeholders.elementary_low;
};

const ChatWithSessions = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [learningSessionId, setLearningSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  const scrollToBottom = useCallback(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [shouldAutoScroll]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShouldAutoScroll(isNearBottom);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, shouldAutoScroll, scrollToBottom]);

  // 학습 세션 시작
  const startLearningSession = useCallback(async () => {
    try {
      const response = await apiClient.post('/analytics/session/start', {
        gradeLevel: mapRoleToGradeLevel(user?.role)
      });
      
      if (response.data.success) {
        setLearningSessionId(response.data.sessionId);
        console.log('📚 학습 세션 시작:', response.data.sessionId);
      }
    } catch (error) {
      console.error('학습 세션 시작 오류:', error);
    }
  }, [user]);

  // 학습 세션 종료
  const endLearningSession = useCallback(async () => {
    if (!learningSessionId) return;
    
    try {
      await apiClient.post('/analytics/session/end', {
        sessionId: learningSessionId
      });
      console.log('📚 학습 세션 종료:', learningSessionId);
      setLearningSessionId(null);
    } catch (error) {
      console.error('학습 세션 종료 오류:', error);
    }
  }, [learningSessionId]);

  // 학습 상호작용 기록
  const recordInteraction = useCallback(async (type, content, response) => {
    if (!learningSessionId) return;
    
    try {
      await apiClient.post('/analytics/interaction', {
        sessionId: learningSessionId,
        type,
        content,
        response
      });
      console.log('📝 학습 상호작용 기록됨');
    } catch (error) {
      console.error('학습 상호작용 기록 오류:', error);
    }
  }, [learningSessionId]);

  const createInitialSession = useCallback(async () => {
    try {
      const response = await apiClient.post('/sessions', {
        title: '새로운 대화'
      });
      
      const newSession = response.data.session;
      setSessions([newSession]);
      setCurrentSessionId(newSession.id);
      
      // 학습 세션 시작
      await startLearningSession();
      
      // 환영 메시지 추가
      const welcomeText = getWelcomeMessage(mapRoleToGradeLevel(user?.role), user?.username);
      const welcomeMessage = {
        id: 'welcome-' + Date.now(),
        role: 'assistant',
        content: welcomeText,
        createdAt: new Date(),
        isFiltered: false
      };
      setMessages([welcomeMessage]);
      
      console.log('✅ 초기 세션 생성 완료:', newSession.id);
    } catch (error) {
      console.error('❌ 초기 세션 생성 오류:', error);
      toast.error('세션 생성에 실패했습니다.');
    }
  }, [user, startLearningSession]);

  const loadSessions = useCallback(async () => {
    try {
      const response = await apiClient.get('/sessions');
      setSessions(response.data.sessions);
      
      // 세션이 없으면 자동으로 첫 세션 생성
      if (response.data.sessions.length === 0) {
        await createInitialSession();
      } else {
        // 가장 최근 세션을 현재 세션으로 설정
        const latestSession = response.data.sessions[0];
        setCurrentSessionId(latestSession.id);
        loadSessionMessages(latestSession.id);
      }
    } catch (error) {
      console.error('세션 로드 오류:', error);
      // 세션 로드 실패 시에도 초기 세션 생성 시도
      await createInitialSession();
    }
  }, [createInitialSession]);

  // 세션 목록 로드
  useEffect(() => {
    if (user) {
      loadSessions();
    }
  }, [user, loadSessions]);

  // 초기 환영 메시지 설정
  useEffect(() => {
    if (user && !currentSessionId) {
      setSuggestions(getSuggestions(mapRoleToGradeLevel(user.role)));
    }
  }, [user, currentSessionId]);

  // 컴포넌트 언마운트 시 학습 세션 종료
  useEffect(() => {
    return () => {
      if (learningSessionId) {
        endLearningSession();
      }
    };
  }, [learningSessionId, endLearningSession]);

  const loadSessionMessages = async (sessionId) => {
    try {
      const response = await apiClient.get(`/sessions/${sessionId}`);
      const messages = response.data.messages || [];
      
      // 서버의 sender 필드를 role 필드로 매핑
      const formattedMessages = messages.map(msg => ({
        id: msg.id,
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.message,
        createdAt: msg.createdAt,
        isFiltered: false
      }));
      
      setMessages(formattedMessages);
    } catch (error) {
      console.error('메시지 로드 오류:', error);
      setMessages([]);
    }
  };

  const handleSessionSelect = (sessionId) => {
    // 세션 변경 시 자동 스크롤 일시 비활성화
    setShouldAutoScroll(false);
    setCurrentSessionId(sessionId);
    loadSessionMessages(sessionId);
    // 메시지 로드 후 자동 스크롤 재활성화
    setTimeout(() => setShouldAutoScroll(true), 500);
  };

  const handleSessionCreate = async (newSession) => {
    setSessions(prev => [newSession, ...prev]);
    setCurrentSessionId(newSession.id);
    setMessages([]);
    
    // 새 세션에 환영 메시지 추가
    if (user) {
      const welcomeMessage = {
        id: 'welcome-' + Date.now(),
        role: 'assistant',
        content: getWelcomeMessage(mapRoleToGradeLevel(user.role), user.username),
        createdAt: new Date(),
        isFiltered: false
      };
      setMessages([welcomeMessage]);
    }
  };

  const handleSessionDelete = (sessionId) => {
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    
    if (currentSessionId === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      if (remainingSessions.length > 0) {
        handleSessionSelect(remainingSessions[0].id);
      } else {
        setCurrentSessionId(null);
        setMessages([]);
      }
    }
  };

  const handleSessionUpdate = (sessionId, updates) => {
    setSessions(prev => prev.map(s => 
      s.id === sessionId ? { ...s, ...updates } : s
    ));
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const messageText = inputMessage.trim();

    // 1. 사용자 메시지와 AI 응답 placeholder 준비
    const tempUserMessageId = `temp-user-${Date.now()}`;
    const userMessage = {
      id: tempUserMessageId,
      role: 'user',
      content: messageText,
      createdAt: new Date()
    };

    // 2. AI 응답용 빈 메시지 준비 (스트리밍 표시)
    const tempAiMessageId = `temp-ai-${Date.now()}`;
    const tempAiMessage = {
      id: tempAiMessageId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      createdAt: new Date()
    };

    // 3. flushSync로 모든 상태 변경을 즉시 렌더링 (API 호출 전에 UI 업데이트)
    flushSync(() => {
      setInputMessage('');
      setIsLoading(true);
      setMessages(prev => [...prev, userMessage, tempAiMessage]);
    });

    try {
      // 4. 스트리밍 API 호출
      const token = localStorage.getItem('token');
      const requestData = {
        message: messageText
      };

      if (currentSessionId) {
        requestData.sessionId = currentSessionId;
      }

      // AbortController로 타임아웃 구현
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 120초 타임아웃

      try {
        const response = await fetch(`${baseURL}/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestData),
          signal: controller.signal
        });

        clearTimeout(timeoutId); // 성공 시 타임아웃 취소

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let sessionId = currentSessionId;
      let finalUserMessageId = tempUserMessageId;
      let finalAiMessageId = tempAiMessageId;

      // 5. 스트리밍 데이터 처리
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              // 세션 ID 업데이트
              if (data.sessionId && !currentSessionId) {
                sessionId = data.sessionId;
                setCurrentSessionId(data.sessionId);
                await loadSessions();
                if (!learningSessionId) {
                  await startLearningSession();
                }
              }

              // 사용자 메시지 ID 업데이트
              if (data.userMessageId) {
                finalUserMessageId = data.userMessageId;
              }

              // AI 메시지 ID 업데이트
              if (data.aiMessageId) {
                finalAiMessageId = data.aiMessageId;
              }

              // 스트리밍 청크 처리
              if (data.chunk) {
                fullContent += data.chunk;
                // 실시간으로 메시지 업데이트
                setMessages(prev => prev.map(msg =>
                  msg.id === tempAiMessageId
                    ? { ...msg, content: fullContent }
                    : msg
                ));
              }

              // 스트리밍 완료 시 최종 메시지 ID로 업데이트
              if (data.done) {
                setMessages(prev => prev.map(msg => {
                  if (msg.id === tempUserMessageId) {
                    return { ...msg, id: finalUserMessageId };
                  }
                  if (msg.id === tempAiMessageId) {
                    return {
                      ...msg,
                      id: finalAiMessageId,
                      content: fullContent,
                      isStreaming: false
                    };
                  }
                  return msg;
                }));

                // 학습 상호작용 기록
                await recordInteraction('chat', messageText, fullContent);

                // 세션 정보 업데이트
                setSessions(prev => prev.map(s =>
                  s.id === sessionId
                    ? { ...s, messageCount: s.messageCount + 2, updatedAt: new Date() }
                    : s
                ));
              }
            } catch (e) {
              console.error('스트리밍 데이터 파싱 오류:', e);
            }
          }
        }
      }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        throw fetchError; // outer catch로 전달
      }
    } catch (error) {
      console.error('메시지 전송 오류:', error);

      // 에러 발생 시 임시 메시지 제거
      setMessages(prev => prev.filter(msg =>
        msg.id !== tempUserMessageId && msg.id !== tempAiMessageId
      ));

      // 에러 타입별 처리
      if (error.name === 'AbortError') {
        // 타임아웃 발생
        toast.error('⏱️ 응답 시간이 초과되었습니다. 더 짧은 질문으로 다시 시도해주세요.');
      } else if (error.response?.status === 400 && error.response?.data?.filtered) {
        // 콘텐츠 필터링
        const filterMessage = {
          id: 'filter-' + Date.now(),
          role: 'system',
          content: '⚠️ 이 메시지는 교육 목적에 적합하지 않아 전송할 수 없습니다.\n다른 주제로 대화해보세요!',
          createdAt: new Date(),
          filterType: 'warning'
        };
        setMessages(prev => [...prev, filterMessage]);
        toast.warning('부적절한 내용이 감지되었습니다.');
      } else if (error.response?.status === 503 || error.response?.status === 504) {
        // 서버 타임아웃 또는 일시적 오류
        toast.error('🔧 서버가 일시적으로 응답하지 않습니다. 잠시 후 다시 시도해주세요.');
      } else if (!navigator.onLine) {
        // 네트워크 오프라인
        toast.error('📡 인터넷 연결을 확인해주세요.');
      } else if (error.message?.includes('Failed to fetch')) {
        // 네트워크 에러
        toast.error('🌐 네트워크 연결에 문제가 발생했습니다. 다시 시도해주세요.');
      } else {
        // 기타 에러
        toast.error('❌ 메시지 전송에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* 타이핑 인디케이터 스타일 */}
      <style>{typingIndicatorStyles}</style>

      {/* 세션 관리 사이드바 */}
      <SessionManager
        sessions={sessions}
        currentSessionId={currentSessionId}
        onSessionSelect={handleSessionSelect}
        onSessionCreate={handleSessionCreate}
        onSessionDelete={handleSessionDelete}
        onSessionUpdate={handleSessionUpdate}
      />

      {/* 채팅 영역 */}
      <div className="flex-1 flex flex-col">
        {/* 채팅 헤더 */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center">
            <SparklesIcon className="w-6 h-6 text-blue-500 mr-2" />
            <h1 className="text-xl font-semibold text-gray-800">
              SafeAI 채팅
            </h1>
            {currentSessionId && (
              <span className="ml-4 text-sm text-gray-500">
                현재 세션: {sessions.find(s => s.id === currentSessionId)?.title}
              </span>
            )}
          </div>
        </div>

        {/* 메시지 영역 */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-4"
          onScroll={handleScroll}
        >
          {messages.length === 0 && !currentSessionId ? (
            <div className="text-center py-12">
              <FaceSmileIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                안녕하세요, {user?.username}님!
              </h3>
              <p className="text-gray-500 mb-6">
                새로운 대화를 시작하거나 기존 대화를 선택해주세요.
              </p>
              
              {/* 추천 질문 */}
              <div className="max-w-2xl mx-auto">
                <h4 className="text-sm font-medium text-gray-700 mb-3">추천 질문</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {suggestions.map((suggestion, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <span className="text-sm text-gray-700">{suggestion}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                gradeLevel={mapRoleToGradeLevel(user?.role)}
              />
            ))
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 입력 영역 */}
        <div className="bg-white border-t border-gray-200 p-4">
          {/* 로딩 상태 표시 */}
          {isLoading && (
            <div className="flex items-center justify-center mb-3 text-sm text-gray-600 animate-pulse">
              <div className="flex items-center gap-2">
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
                <span>AI가 답변을 생성하고 있습니다...</span>
              </div>
            </div>
          )}

          <div className="flex items-end space-x-3 max-w-4xl mx-auto">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={getPlaceholder(mapRoleToGradeLevel(user?.role))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="1"
                style={{
                  minHeight: '48px',
                  maxHeight: '120px'
                }}
                disabled={isLoading}
              />
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="flex items-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="메시지 전송"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
              <span className="hidden sm:inline">전송</span>
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWithSessions;
